import classNames from 'classnames';
// import gtm from 'eco-design-reliance/gtm';
import { BtnDoubleProps } from 'eco-design-micro/props/btn/BtnDoubleProps';
import { TitleDescProps } from 'eco-design-micro/props/msg/TitleDescProps';
import { PriceMarkProps } from 'eco-design-micro/props/price/PriceMarkProps';
import { PriceProps } from 'eco-design-micro/props/price/PriceProps';
import { formatCountryCurrency } from 'eco-design-micro/utils/currency';
import getLocal from 'eco-design-reliance/getLocal';
import EcoButtonDouble from 'eco-design/base/eco-button-double';
import React from 'react';
import { Autoplay, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import EcoImg, { EcoImgProps } from '../../base/eco-img/index';
import EcoSectionCommon, { EcoSectionCommonProps } from '../../elements/eco-section-common/index';
import { Point } from './icons';
import './index.scss';

/**
 * @param name EcoBundle2 组件
 * @param description JP常用的bundle组件，如R3, D3，DP3, Rapid等
 * @param author kalkin
 * @param date 2025-03-01
 * @param version 1.0.0
 */
export interface EcoBundle2ItemInfoProps extends PriceMarkProps, TitleDescProps, PriceProps, BtnDoubleProps {
    tagText?: string;
    tagFontColor?: string;
    tagBgColor?: string;
    pricePrefix?: string;
    priceSuffix?: string;
    priceComparePrefix?: string;
    priceCompareSuffix?: string;
    pointsTimes?: number; // 积分倍数 积分 = 价格 / 积分除数 * 积分倍数
    pointsDivide?: number; // 积分除数
}

export interface EcoBundle2ItemProps {
    direction?: 'ltr' | 'rtl';
    imgs: EcoImgProps[];
    info: EcoBundle2ItemInfoProps;
}

interface EcoBundle2Props {
    className?: string;
    theme?: 'white' | 'black';
    section?: EcoSectionCommonProps;
    loop?: boolean;
    autoplay?: boolean;
    items: EcoBundle2ItemProps[];
}

// 获取千分位数字
const getThousandNum = (num: number) => {
    const str = num.toString();
    const reg = /(?=(\B)(\d{3})+$)/g;
    return str.replace(reg, ',');
};

const EcoBundle2: React.FC<EcoBundle2Props> = ({ className, theme = 'white', loop = true, autoplay, section, items }) => {
    const prefixCls = `eco-bundle-2`;
    const hasSection = !!section;
    const classes = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-${theme}`]: theme,
        [`${prefixCls}-has-section`]: hasSection,
        [`${prefixCls}-no-section`]: !hasSection,
    });
    const { locale = 'jp' } = getLocal();

    return (
        <div className={classes}>
            {hasSection && <EcoSectionCommon theme={theme} {...section} />}
            <ul className={`${prefixCls}-items`}>
                {items?.map((item: EcoBundle2ItemProps, k: number) => {
                    const { info, imgs, direction } = item;
                    const itemStyleComp = {
                        '--eco-bundle-2-item-flexDirection': direction === 'rtl' ? 'row-reverse' : '',
                    } as React.CSSProperties;
                    const IS_MULTIPLE = imgs?.length > 1;
                    const {
                        discountTextL,
                        tagText,
                        title,
                        desc,
                        price,
                        comparePrice,
                        pricePrefix,
                        priceSuffix,
                        priceComparePrefix,
                        priceCompareSuffix,
                        pointsTimes,
                        pointsTextL,
                        pointsTextR = locale === 'jp' ? 'ポイント' : 'Points',
                        pointsDivide = 150,
                        ...rest
                    } = info;

                    return (
                        <li className={`${prefixCls}-item ${prefixCls}-item-${IS_MULTIPLE ? 'multiple' : 'single'}`} key={k} style={itemStyleComp}>
                            <Swiper
                                className={`${prefixCls}-item-imgs ${prefixCls}-item-imgs-${theme} ${prefixCls}-item-imgs-${imgs?.length}`}
                                modules={[Navigation, Pagination, Autoplay]}
                                navigation={IS_MULTIPLE}
                                pagination={IS_MULTIPLE ? { clickable: true } : false}
                                loop={loop}
                                autoplay={autoplay}
                                spaceBetween={0}
                                slidesPerView={1}
                            >
                                {imgs?.map((img: EcoImgProps, m: number) => (
                                    <SwiperSlide className={`${prefixCls}-item-img`} key={`${k}_${m}`}>
                                        <EcoImg {...img} />
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                            <div className={`${prefixCls}-item-info${IS_MULTIPLE ? '' : ` ${prefixCls}-item-info-single`} ${prefixCls}-item-info-${theme}`}>
                                <div className={`${prefixCls}-item-info-content`}>
                                    {(tagText || discountTextL || pointsTextL) && (
                                        <div className={`${prefixCls}-item-info-marker`}>
                                            {discountTextL && <span className={`${prefixCls}-item-info-marker-discount`}>{discountTextL}</span>}
                                            {(pointsTextL || pointsTextR) && pointsTimes && (
                                                <div className={`${prefixCls}-item-info-marker-point`}>
                                                    <Point />
                                                    <span className={`${prefixCls}-item-info-marker-point-times`}>
                                                        {pointsTimes}
                                                        {pointsTextL}
                                                    </span>
                                                    <span className={`${prefixCls}-item-info-marker-point-text`}>
                                                        {getThousandNum(Math.round((Number(price) * (Number(pointsTimes) ?? 5)) / pointsDivide)) + pointsTextR}
                                                    </span>
                                                </div>
                                            )}
                                            {tagText && <span className={`${prefixCls}-item-info-marker-tag`}>{tagText}</span>}
                                        </div>
                                    )}
                                    {(title || desc) && (
                                        <div className={`${prefixCls}-item-info-section`}>
                                            {title && <p className={`${prefixCls}-item-info-section-title`}>{title}</p>}
                                            {desc && <p className={`${prefixCls}-item-info-section-desc`}>{desc}</p>}
                                        </div>
                                    )}
                                    {(price || comparePrice) && (
                                        <div className={`${prefixCls}-item-info-price`}>
                                            {price && (
                                                <span className={`${prefixCls}-item-info-price-price`}>
                                                    {pricePrefix}
                                                    {formatCountryCurrency(price, locale)}
                                                    {priceSuffix}
                                                </span>
                                            )}
                                            {comparePrice && (
                                                <span className={`${prefixCls}-item-info-price-compare`}>
                                                    {priceComparePrefix}
                                                    {formatCountryCurrency(comparePrice, locale)}
                                                    {priceCompareSuffix || priceSuffix}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {(!!rest?.lChildren || !!rest?.rChildren) && (
                                        <div className={`${prefixCls}-item-info-btn`}>
                                            <EcoButtonDouble {...rest} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default EcoBundle2;
